import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  ApiKeysRequestError,
  createApiKey,
  deleteApiKey,
  listApiKeys,
  rotateApiKey,
  type ApiKeyRow,
} from "@/features/api-keys/api/apiKeys";
import { SettingsSection } from "@/features/settings/components/SettingsSection";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Check, Copy } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

/** Matches server `ApiKeyCreate.name` max_length. */
const KEY_NAME_MAX_LEN = 255;
/** Letters, numbers, underscores, and dashes only. */
const KEY_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

/** Keys issued from this site grant access to all GXL MCP services. */
const PAPERCLIP_API_KEY_SCOPES = ["*"] as const;

export interface ApiKeyListItem {
  id: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  enabled: boolean;
}

const mapApiRow = (row: ApiKeyRow): ApiKeyListItem => {
  return {
    id: row.id,
    name: row.name,
    keyPrefix: row.key_prefix,
    createdAt: row.created_at,
    enabled: row.enabled,
  };
};

const formatKeyPreview = (prefix: string): string => {
  return `gxl_${prefix}…`;
};

const validateKeyName = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return "Enter a name for this key.";
  if (trimmed.length > KEY_NAME_MAX_LEN) {
    return `Use at most ${KEY_NAME_MAX_LEN} characters.`;
  }
  if (!KEY_NAME_PATTERN.test(trimmed)) {
    return "Use only letters, numbers, underscores (_), and dashes (-).";
  }
  return null;
};

const formatCreatedAt = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

type ConfirmAction = "revoke" | "rotate" | null;

type SecretModalState = { rawKey: string; variant: "create" | "rotate" };

export const ApiKeysContent = () => {
  const [keys, setKeys] = useState<ApiKeyListItem[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [keysError, setKeysError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const nameError = validateKeyName(name);
  const showNameError = nameTouched && nameError !== null;

  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [confirmKey, setConfirmKey] = useState<ApiKeyListItem | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const [secretModal, setSecretModal] = useState<SecretModalState | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const refreshKeys = useCallback(async () => {
    setKeysError(null);
    setKeysLoading(true);
    try {
      const rows = await listApiKeys();
      setKeys(rows.map(mapApiRow));
    } catch (e) {
      const msg =
        e instanceof ApiKeysRequestError
          ? e.message
          : "Could not load keys. Try Refresh again.";
      setKeysError(msg);
    } finally {
      setKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshKeys();
  }, [refreshKeys]);

  useEffect(() => {
    if (!secretModal) setSecretCopied(false);
  }, [secretModal]);

  const openConfirm = (action: Exclude<ConfirmAction, null>, key: ApiKeyListItem) => {
    setConfirmError(null);
    setConfirmAction(action);
    setConfirmKey(key);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (confirmBusy) return;
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmKey(null);
    setConfirmError(null);
  };

  const handleConfirmAction = async () => {
    if (!confirmKey || !confirmAction) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      if (confirmAction === "revoke") {
        await deleteApiKey(confirmKey.id);
        setConfirmOpen(false);
        setConfirmAction(null);
        setConfirmKey(null);
        await refreshKeys();
      } else {
        const { rawKey } = await rotateApiKey(confirmKey.id);
        setConfirmOpen(false);
        setConfirmAction(null);
        setConfirmKey(null);
        setSecretModal({ rawKey, variant: "rotate" });
        await refreshKeys();
      }
    } catch (e) {
      const msg =
        e instanceof ApiKeysRequestError ? e.message : "Something went wrong. Try again.";
      setConfirmError(msg);
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameTouched(true);
    setCreateError(null);
    if (validateKeyName(name) !== null) return;

    const trimmed = name.trim();
    setCreateBusy(true);
    try {
      const { rawKey } = await createApiKey({
        name: trimmed,
        scopes: [...PAPERCLIP_API_KEY_SCOPES],
      });
      setName("");
      setNameTouched(false);
      setSecretModal({ rawKey, variant: "create" });
      await refreshKeys();
    } catch (err) {
      const msg =
        err instanceof ApiKeysRequestError ? err.message : "Could not create key. Try again.";
      setCreateError(msg);
    } finally {
      setCreateBusy(false);
    }
  };

  const handleCopySecret = async (rawKey: string) => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setSecretCopied(true);
    } catch (e) {
      setSecretCopied(false);
    }
  };

  return (
    <>
      <div>
        <p className="max-w-xl text-base leading-7 text-zinc-600">
          Keys grant programmatic access to Paperclip — the same commands as the CLI,
          from your own scripts.{" "}
          <Link to="/docs" className="text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-900">
            Read the docs
          </Link>
          .
        </p>
        <p className="mt-3 mb-7 max-w-xl border-l-2 border-amber-600 pl-4 text-sm leading-6 text-zinc-500" role="note">
          Treat keys like passwords: keep them out of git, chat and client-side code.
          Revoke a key immediately if it may have leaked.
        </p>

        <SettingsSection id="settings-keys-list" label="Your keys">
          {keysError && (
            <p className="mb-3 text-sm text-red-700" role="alert">{keysError}</p>
          )}
          {keysLoading ? (
            <p className="text-sm italic text-zinc-400">Loading keys…</p>
          ) : keys.length === 0 ? (
            <p className="text-sm italic text-zinc-400">No keys yet. Create one below.</p>
          ) : (
            <table className="w-full text-sm leading-6">
              <thead>
                <tr className="font-ui border-b border-zinc-200 text-left text-zinc-400">
                  <th className="py-2 pr-4 font-normal">Name</th>
                  <th className="hidden py-2 pr-4 font-normal md:table-cell">Key</th>
                  <th className="hidden py-2 pr-4 font-normal sm:table-cell">Created</th>
                  <th className="py-2 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id} className="group border-b border-zinc-200/60 last:border-b-0">
                    <td className="py-2.5 pr-4">
                      <span className="text-zinc-900">{key.name}</span>
                      <p className="hero-mono text-xs text-zinc-400 md:hidden">
                        {formatKeyPreview(key.keyPrefix)}
                      </p>
                    </td>
                    <td className="hidden py-2.5 pr-4 md:table-cell">
                      <span className="hero-mono text-xs text-zinc-500">{formatKeyPreview(key.keyPrefix)}</span>
                    </td>
                    <td className="hidden whitespace-nowrap py-2.5 pr-4 text-zinc-500 sm:table-cell">
                      {formatCreatedAt(key.createdAt)}
                    </td>
                    <td className="py-2.5 text-right">
                      <span className="flex items-baseline justify-end gap-4 text-sm">
                        <button
                          type="button"
                          onClick={() => openConfirm("rotate", key)}
                          className="text-zinc-400 transition-colors hover:text-zinc-900"
                        >
                          Rotate
                        </button>
                        <button
                          type="button"
                          onClick={() => openConfirm("revoke", key)}
                          aria-label={`Revoke ${key.name}`}
                          className="text-zinc-400 transition-colors hover:text-red-700"
                        >
                          Revoke
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => refreshKeys()}
              disabled={keysLoading}
              className="font-ui text-sm text-zinc-400 transition-colors hover:text-zinc-900 disabled:opacity-50"
            >
              {keysLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </SettingsSection>

        <SettingsSection id="settings-keys-create" label="New key">
          <form onSubmit={(e) => handleCreate(e)}>
            <div className="flex items-baseline gap-4 border-b border-zinc-300 pb-1.5 focus-within:border-zinc-900">
              <label htmlFor="key-name" className="shrink-0 text-2xs uppercase tracking-[0.18em] text-zinc-400">
                Name
              </label>
              <input
                id="key-name"
                type="text"
                autoComplete="off"
                value={name}
                maxLength={KEY_NAME_MAX_LEN}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setNameTouched(true)}
                placeholder="e.g. laptop-dev"
                aria-invalid={showNameError || undefined}
                aria-describedby={showNameError ? "key-name-error" : undefined}
                className="hero-mono min-w-0 flex-1 bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-300"
              />
              <button
                type="submit"
                disabled={createBusy || name.trim().length === 0}
                className="font-ui shrink-0 text-sm text-zinc-900 transition-colors disabled:text-zinc-300"
              >
                {createBusy ? "Creating…" : "Create key ↑"}
              </button>
            </div>
            <p className="mt-2 text-xs leading-5 text-zinc-400">
              Letters, numbers, <code className="hero-mono">_</code> and <code className="hero-mono">-</code>; up to {KEY_NAME_MAX_LEN} characters.
              The secret is shown once, on creation.
            </p>
            {showNameError && (
              <p id="key-name-error" role="alert" className="mt-1.5 text-sm text-red-700">
                {nameError}
              </p>
            )}
            {createError && (
              <p role="alert" className="mt-1.5 text-sm text-red-700">
                {createError}
              </p>
            )}
          </form>
        </SettingsSection>
      </div>

      <Dialog
        open={confirmOpen && !!confirmKey && !!confirmAction}
        onOpenChange={(open) => {
          if (!open) closeConfirm();
        }}
      >
        <DialogContent hideCloseButton className="max-w-md p-5">
          <DialogHeader>
            <DialogTitle className="text-base">
              {confirmAction === "revoke" ? "Revoke API key?" : "Rotate API key?"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-600 leading-relaxed">
              {confirmAction === "revoke" ? (
                <>
                  <span className="font-medium text-zinc-900">{confirmKey?.name}</span> will stop working
                  immediately. This cannot be undone.
                </>
              ) : (
                <>
                  A new secret will be issued for <span className="font-medium text-zinc-900">{confirmKey?.name}</span>.
                  The current secret will stop working. You will need to update any clients using the old secret.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {confirmError && (
            <p className="mt-3 text-sm text-red-700" role="alert">
              {confirmError}
            </p>
          )}
          <DialogFooter className="pt-0 mt-5">
            <Button variant="outline" onClick={closeConfirm} disabled={confirmBusy}>
              Cancel
            </Button>
            <Button
              variant={confirmAction === "revoke" ? "destructive" : "default"}
              onClick={() => handleConfirmAction()}
              loading={confirmBusy}
            >
              {confirmBusy ? "Working…" : confirmAction === "revoke" ? "Revoke key" : "Rotate key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!secretModal}
        onOpenChange={(open) => {
          if (!open) setSecretModal(null);
        }}
      >
        <DialogContent hideCloseButton className="max-w-lg p-5">
          <DialogHeader>
            <DialogTitle className="text-base">
              {secretModal?.variant === "create" ? "Save your API key" : "Your API key was rotated"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-600 leading-relaxed">
              {secretModal?.variant === "create"
                ? "Copy this key now. For security, we only show the full secret once. After you close this dialog you will only see a short prefix in your list."
                : "Copy the new key below. The previous secret no longer works. We will only show the full secret this one time."}
            </DialogDescription>
          </DialogHeader>
          <pre className="mt-4 flex-1 text-xs bg-zinc-100 border border-zinc-200 rounded-[3px] p-3 overflow-x-auto whitespace-pre-wrap break-all text-zinc-900">
            {secretModal?.rawKey}
          </pre>
          <DialogFooter className="pt-0 mt-4">
            <Button
              variant="outline"
              onClick={() => secretModal && handleCopySecret(secretModal.rawKey)}
            >
              {secretCopied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy key
                </>
              )}
            </Button>
            <Button onClick={() => setSecretModal(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ApiKeysPage = () => {
  useDocumentTitle("API keys");
  return (
    <div className="h-full overflow-y-auto bg-zinc-50">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">API keys</h1>
        <ApiKeysContent />
      </div>
    </div>
  );
};
